import { ColumnType, ListingHelper } from 'src/app/core/utils/listing.helper';
import { Component, EventEmitter, HostBinding, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { Filter, FilterService } from '@brafton/angular-framework-library';
import { Subscription } from 'rxjs';
import { Sort } from '@angular/material/sort';
import { ActivatedRoute, Router } from '@angular/router';
import { IBaseProject } from '@brafton/common-framework-library';

@Component({
  selector: 'shared-master-projects-listing',
  templateUrl: './master-projects-listing.component.html',
  styleUrls: ['./master-projects-listing.component.scss'],
  host: { class: 'master-projects-listing' },
})
export class SharedMasterProjectsListingComponent implements OnInit, OnChanges, OnDestroy {
  @Input() projects!: any;
  @Input() disableProgressLine = false;
  @Input() showProgress = true;
  @Input() showOrgInTitle = false;
  @Input() defaultFilters?: Filter[];
  @Input() groupBy?: 'owner' | 'month' | 'status' | 'organization';
  @Input() sort!: Filter[];
  @Output() sortChange: EventEmitter<Filter[]> = new EventEmitter<Filter[]>();
  @HostBinding('class.grouped') get groupingChecker() {
    return this.groupBy !== undefined;
  }
  @Input() get showUnits() {
    return this.columnBuilder.isShown('units');
  }
  set showUnits(value: boolean) {
    this.columnBuilder.setShown('units', value);
  }

  @Input() get showOwner() {
    return this.columnBuilder.isShown('owner');
  }
  set showOwner(value: boolean) {
    this.columnBuilder.setShown('owner', value);
  }

  @Input() get showContentType() {
    return this.columnBuilder.isShown('contentType');
  }
  set showContentType(value: boolean) {
    this.columnBuilder.setShown('contentType', value);
  }

  @Input() get showDate() {
    return this.columnBuilder.isShown('date');
  }
  set showDate(value: boolean) {
    this.columnBuilder.setShown('date', value);
  }

  @Input() get showOrganization() {
    return this.columnBuilder.isShown('organization');
  }
  set showOrganization(value: boolean) {
    this.columnBuilder.setShown('organization', value);
  }

  @Input() get showStatus() {
    return this.columnBuilder.isShown('status');
  }
  set showStatus(value: boolean) {
    this.columnBuilder.setShown('status', value);
  }

  @Input() get showActions() {
    return this.columnBuilder.isShown('actions');
  }
  set showActions(value: boolean) {
    this.columnBuilder.setShown('actions', value);
  }

  @Input() showColumnHeaders = true;
  @Input() expandable = true;

  // A placeholder for all subscripton so we can unsubscribe all of them in the ondestroy Methode.
  subscriptions = new Subscription();
  columnBuilder = new ListingHelper();

  constructor(private filterService: FilterService, private router: Router, private activatedRoute: ActivatedRoute) {
    this.columnBuilder.addColumn('image', ColumnTypes.Small, true);
    this.columnBuilder.addDefaultColumn('default');
    this.columnBuilder.addColumn('organization', ColumnTypes.Large);
    this.columnBuilder.addColumn('owner', ColumnTypes.Large);
    this.columnBuilder.addColumn('date', ColumnTypes.Medium);
    this.columnBuilder.addColumn('contentType', ColumnTypes.Large);
    this.columnBuilder.addColumn('units', ColumnTypes.Medium);
    this.columnBuilder.addColumn('status', ColumnTypes.Medium);
    this.columnBuilder.addColumn('actions', ColumnTypes.XSmall);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['projects']) {
      switch (this.groupBy) {
        case 'owner':
          this.projects.items = this.groupByOwner(this.projects.items);
          break;
        case 'month':
          this.projects.items = this.groupByMonth(this.projects.items);
          break;
        case 'organization':
          this.projects.items = this.groupByFeed(this.projects.items);
          break;
        case 'status':
          this.projects.items = this.groupByStatus(this.projects.items);
          break;
      }
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  ngOnInit(): void {
    this.subscriptions.add(this.filterService.registerFilter('sort', this.defaultFilters).subscribe((r: Filter[]) => {
      this.sort = r;
      this.sortChange.emit(this.sort);
    }));
  }

  groupByOwner(items: IBaseProject[]): IBaseProject[] | Group[] {
    const groupsRemoved = items.filter((i: any) => !i.group);
    const results = groupsRemoved.reduce((acc: any[], cur: IBaseProject, index: number) => {
      if (index === 0 || cur.owner.id !== groupsRemoved[index - 1].owner.id) {
        const y = acc.concat([{ group: cur.owner, field: 'owner' } as Group, cur]);
        return y;
      } else {
        const z = acc.concat(cur);
        return z;
      }
    }, []);
    return results;
  }

  groupByMonth(items: IBaseProject[]): IBaseProject[] | Group[] {
    const groupsRemoved = items.filter((i: any) => !i.group);
    const results = groupsRemoved.reduce((acc: any[], cur: IBaseProject, index: number) => {
      if (index === 0 || new Date((cur as any).projectEndDate).toISOString().substring(0, 7) !== new Date((groupsRemoved[index - 1] as any).projectEndDate).toISOString().substring(0, 7)) {
        const y = acc.concat([{ group: new Date((cur as any).projectEndDate), field: 'date' } as Group, cur]);
        return y;
      } else {
        const z = acc.concat(cur);
        return z;
      }
    }, []);
    return results;
  }

  groupByFeed(items: IBaseProject[]): IBaseProject[] | Group[] {
    const groupsRemoved = items.filter((i: any) => !i.group);
    const results = groupsRemoved.reduce((acc: any[], cur: IBaseProject, index: number) => {
      if (index === 0 || (cur as any).feedDetails.feedId !== (groupsRemoved[index - 1] as any).feedDetails.feedId) {
        const y = acc.concat([{ group: ((cur as any).feedDetails), field: 'organization' } as Group, cur]);
        return y;
      } else {
        const z = acc.concat(cur);
        return z;
      }
    }, []);
    return results;
  }

  groupByStatus(items: IBaseProject[]): IBaseProject[] | Group[] {
    const groupsRemoved = items.filter((i: any) => !i.group);
    const results = groupsRemoved.reduce((acc: any[], cur: IBaseProject, index: number) => {
      if (index === 0 || cur.status.id !== groupsRemoved[index - 1].status.id) {
        const y = acc.concat([{ group: (cur.status), field: 'status' } as Group, cur]);
        return y;
      } else {
        const z = acc.concat(cur);
        return z;
      }
    }, []);
    return results;
  }

  updateSort(sort: Sort) {
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { sort: (sort.direction === '' ? undefined : sort.active + '-' + sort.direction + ':' + sort.active) },
      queryParamsHandling: 'merge',
    });
  }

  buildSort(): Sort | undefined {
    if (this.sort?.length > 0) {
      const active = this.sort[0].value.split('-')[0];
      const direction = this.sort[0].value.split('-')[1];
      return { active: active, direction: direction } as Sort;
    }
    return;
  }
}

class ColumnTypes {
  static readonly XSmall: ColumnType = new ColumnType(3, 3);
  static readonly Small: ColumnType = new ColumnType(5, 9);
  static readonly Medium: ColumnType = new ColumnType(10, 20);
  static readonly Large: ColumnType = new ColumnType(12, 24);
}

interface Group {
  group: any;
  field: string;
}
